/* eslint-disable no-console --
 * This module IS the sanctioned console wrapper. CLAUDE.md routes all debug
 * logging through `@shared/vue/utils/debug` precisely so that `console.*` calls
 * live in exactly one file, behind the environment and platform gates below.
 * The rule stays on everywhere else; disabling it here is what lets it stay on
 * there.
 */

/**
 * Platform detection utilities
 * Note: VITE_APP_ENV can be 'android.staging', 'android.prod', 'ios', etc.
 */
const isAndroid = (): boolean => {
  const env = import.meta.env.VITE_APP_ENV || '';
  return env.startsWith('android');
};
const isIOS = (): boolean => {
  const env = import.meta.env.VITE_APP_ENV || '';
  return env === 'ios' || env.startsWith('ios.');
};
const isNativeApp = (): boolean => isAndroid() || isIOS();

/**
 * Get platform name for logging prefix
 */
const getPlatformPrefix = (): string => {
  if (isAndroid()) return '[ANDROID]';
  if (isIOS()) return '[IOS]';
  return '[WEB]';
};

/**
 * Format arguments for logging - handles objects and arrays for better Android logcat output
 */
const formatArgs = (args: unknown[]): string => {
  if (args.length === 0) return '';
  return args
    .map((arg) => {
      if (arg === null) return 'null';
      if (arg === undefined) return 'undefined';
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg, null, isNativeApp() ? 0 : 2);
        } catch {
          // Fallback for circular references or other stringify failures
          return '[Object]';
        }
      }
      if (typeof arg === 'string') return arg;
      if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
      if (typeof arg === 'bigint') return `${arg}n`;
      if (typeof arg === 'symbol') return arg.toString();
      if (typeof arg === 'function') return '[Function]';
      return '[Unknown]';
    })
    .join(' ');
};

/**
 * Safe console utilities that check for console availability before use
 * Prevents errors in environments where console might be unavailable
 * On Android/iOS, logs are formatted for better logcat/Xcode readability
 */
export const safeConsole = {
  log: (message: string, ...args: unknown[]): void => {
    if (typeof console !== 'undefined' && console.log) {
      if (isNativeApp()) {
        const formattedArgs = formatArgs(args);
        console.log(`${getPlatformPrefix()} ${message}${formattedArgs ? ' ' + formattedArgs : ''}`);
      } else {
        console.log(message, ...args);
      }
    }
  },

  warn: (message: string, ...args: unknown[]): void => {
    if (typeof console !== 'undefined' && console.warn) {
      if (isNativeApp()) {
        const formattedArgs = formatArgs(args);
        console.warn(`${getPlatformPrefix()} ${message}${formattedArgs ? ' ' + formattedArgs : ''}`);
      } else {
        console.warn(message, ...args);
      }
    }
  },

  error: (message: string, ...args: unknown[]): void => {
    if (typeof console !== 'undefined' && console.error) {
      if (isNativeApp()) {
        const formattedArgs = formatArgs(args);
        console.error(`${getPlatformPrefix()} ${message}${formattedArgs ? ' ' + formattedArgs : ''}`);
      } else {
        console.error(message, ...args);
      }
    }
  },

  info: (message: string, ...args: unknown[]): void => {
    if (typeof console !== 'undefined' && console.info) {
      if (isNativeApp()) {
        const formattedArgs = formatArgs(args);
        console.info(`${getPlatformPrefix()} ${message}${formattedArgs ? ' ' + formattedArgs : ''}`);
      } else {
        console.info(message, ...args);
      }
    }
  },

  debug: (message: string, ...args: unknown[]): void => {
    if (typeof console !== 'undefined' && console.debug) {
      if (isNativeApp()) {
        const formattedArgs = formatArgs(args);
        console.debug(`${getPlatformPrefix()} ${message}${formattedArgs ? ' ' + formattedArgs : ''}`);
      } else {
        console.debug(message, ...args);
      }
    }
  },

  trace: (message: string, ...args: unknown[]): void => {
    if (typeof console !== 'undefined' && console.trace) {
      if (isNativeApp()) {
        // On native, trace doesn't work well, use log with stack
        const formattedArgs = formatArgs(args);
        console.log(`${getPlatformPrefix()} [TRACE] ${message}${formattedArgs ? ' ' + formattedArgs : ''}`);
        console.log(new Error().stack);
      } else {
        console.trace(message, ...args);
      }
    }
  },
};

/**
 * Get formatted timestamp for logging in local timezone
 * Example: [2025-12-05 21:32:52.123 +01:00]
 */
const getTimestamp = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const ms = String(now.getMilliseconds()).padStart(3, '0');

  return `[${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms}]`;
};

/**
 * Development-only logging that will be stripped in production builds
 * Platform-aware: includes [ANDROID], [IOS], or [WEB] prefix
 */
export const devLog = (message: string, ...args: unknown[]): void => {
  if (String(import.meta.env.VITE_APP_DEBUG) === 'true') {
    safeConsole.log(`[LOG] ${getTimestamp()} ${message}`, ...args);
  }
};

/**
 * Development-only warning that will be stripped in production builds
 * Platform-aware: includes [ANDROID], [IOS], or [WEB] prefix
 */
export const devWarn = (message: string, ...args: unknown[]): void => {
  if (String(import.meta.env.VITE_APP_DEBUG) === 'true') {
    safeConsole.warn(`[WARN] ${getTimestamp()} ${message}`, ...args);
  }
};

/**
 * Development-only error that will be stripped in production builds
 * Platform-aware: includes [ANDROID], [IOS], or [WEB] prefix
 */
export const devError = (message: string, ...args: unknown[]): void => {
  if (String(import.meta.env.VITE_APP_DEBUG) === 'true') {
    safeConsole.error(`[ERROR] ${getTimestamp()} ${message}`, ...args);
  }
};

/**
 * Development-only trace that will be stripped in production builds
 * Shows the call stack to help debug where a function is being called from
 * Platform-aware: includes [ANDROID], [IOS], or [WEB] prefix
 */
export const devTrace = (message: string, ...args: unknown[]): void => {
  if (String(import.meta.env.VITE_APP_DEBUG) === 'true') {
    safeConsole.trace(`[TRACE] ${getTimestamp()} ${message}`, ...args);
  }
};

/**
 * Export platform detection utilities for use in other modules
 */
export const platform = {
  isAndroid,
  isIOS,
  isNativeApp,
  isWeb: (): boolean => !isNativeApp(),
};
